/**
 * Authentication state management with Supabase Auth
 */

import { writable, derived } from 'svelte/store'
import { supabase } from '../services/supabase.js'

// Create auth state store
function createAuthStore() {
  const { subscribe, set, update } = writable({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true
  })

  return {
    subscribe,

    /**
     * Initialize auth state from Supabase session
     */
    async init() {
      try {
        // Get current session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('Failed to get session:', error)
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false
          })
          return
        }

        if (session && session.user) {
          set({
            isAuthenticated: true,
            user: {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.name || session.user.email
            },
            token: session.access_token,
            loading: false
          })

          // Initialize default exercises if first-time login
          try {
            const { initDefaultExercises } = await import('../services/init.js')
            const initResult = await initDefaultExercises()

            if (initResult.imported) {
              console.log(`Imported ${initResult.count} default exercises`)
            } else {
              console.log(`Using existing ${initResult.count} exercises`)
            }
          } catch (error) {
            console.error('Failed to initialize default exercises:', error)
            // Don't fail init if exercise init fails
          }
        } else {
          set({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false
          })
        }

        // Setup auth state change listener
        supabase.auth.onAuthStateChange((event, session) => {
          console.log('Auth state changed:', event)

          if (event === 'SIGNED_OUT' || !session) {
            set({
              isAuthenticated: false,
              user: null,
              token: null,
              loading: false
            })
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            set({
              isAuthenticated: true,
              user: {
                id: session.user.id,
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.email
              },
              token: session.access_token,
              loading: false
            })
          }
        })
      } catch (error) {
        console.error('Failed to initialize auth state:', error)
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false
        })
      }
    },

    /**
     * Login with credentials
     */
    async login(email, password) {
      try {
        // Import loginUser from api.js
        const { loginUser } = await import('../services/api.js')

        const result = await loginUser(email, password)
        const { token, user } = result

        // Update store (Supabase handles session storage automatically)
        set({
          isAuthenticated: true,
          user,
          token,
          loading: false
        })

        // Initialize default exercises if first-time login
        try {
          const { initDefaultExercises } = await import('../services/init.js')
          const initResult = await initDefaultExercises()

          if (initResult.imported) {
            console.log(`Imported ${initResult.count} default exercises`)
          } else {
            console.log(`Using existing ${initResult.count} exercises`)
          }
        } catch (error) {
          console.error('Failed to initialize default exercises:', error)
          // Don't fail login if exercise init fails
        }

        return { success: true }
      } catch (error) {
        console.error('Login error:', error)
        return { success: false, error: error.message }
      }
    },

    /**
     * Logout
     */
    async logout() {
      try {
        // Sign out from Supabase (this clears the session)
        await supabase.auth.signOut()

        // Clear store
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          loading: false
        })

        return { success: true }
      } catch (error) {
        console.error('Logout error:', error)
        return { success: false, error: error.message }
      }
    },

    /**
     * Clear auth state (e.g., when token expires)
     */
    async clearAuth() {
      await this.logout()
    },

    /**
     * Get current token
     */
    getToken() {
      let currentToken = null
      subscribe(state => {
        currentToken = state.token
      })()
      return currentToken
    }
  }
}

export const auth = createAuthStore()

// Derived store for checking if user is logged in
export const isLoggedIn = derived(auth, $auth => $auth.isAuthenticated)

// Derived store for current user
export const currentUser = derived(auth, $auth => $auth.user)
